/*
 * BuildStatsDashboard — "How your agents work" client surface.
 *
 * Renders the honest Phase-1 build metrics aggregated on validx
 * (GET /agents/projects/:id/build-stats). Lives as the Insights tab inside
 * the client agent workspace (dark theme). Every number traces to a real
 * validx field; sections with no data SUPPRESS themselves (never render 0%
 * or empty axes). No token/model/cost jargon. See build_stats_service +
 * the validx provisioning route for field lineage + honesty rules.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { agentsApi } from '../../../services/agentsApi';

// Brand palette (light workspace, AA brand). Accent yellow used sparingly.
const BLUE = '#4080F2';
const YELLOW = '#FFEA00';
const GREEN = '#3fae2a';
const RED = '#FD2751';
const CARD = '#EEF3FF';      // canonical AA card surface
const TEXT = '#0b0b0f';
const MUTED = '#6b7280';
const TRACK = '#dbe4f7';     // light track for bars / doughnut remainder
const SERIES = [BLUE, '#7aa6ff', YELLOW, GREEN, '#9b7bff', RED];

const TYPE_LABEL: Record<string, string> = {
  BUG_FIX: 'Bugs fixed', FEATURE: 'Features shipped', CHANGE: 'Changes',
  OPTIMIZATION: 'Improvements', INVESTIGATION: 'Investigations', OTHER: 'Other',
};

interface Stats {
  ok: boolean;
  ticketCount: number;
  hero: { filesChanged: number; verificationGatesPassed: number; completedAutonomously: number };
  autonomy: { rate: number | null; autonomous: number; terminal: number; needsExpert: number; needsCustomer: number };
  escalationReasons: { label: string; count: number }[];
  workMix: { type: string; count: number }[];
  momentum: { week: string; planned: number; delivered: number }[];
  depth: { filesChanged: number; commandsRun: number; subtasksTotal: number; subtasksDone: number; prsMerged: number; prsTotal: number };
  verification: { acPassed: number; acFailed: number };
  plannerRigor: { avgFilesDigested: number; avgAcceptanceCriteria: number; coverage: { covered: number; total: number } | null };
  tickets: any[];
  ledger: { type: string; title: string; createdAt: string }[];
}

const Eyebrow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: BLUE, marginBottom: 10 }}>{children}</div>
);

const Card: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <div style={{ background: CARD, borderRadius: 16, padding: 24, ...style }}>{children}</div>
);

const Counter: React.FC<{ label: string; value: React.ReactNode; sub?: string }> = ({ label, value, sub }) => (
  <Card>
    <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: MUTED }}>{label}</div>
    <div style={{ fontSize: 34, fontWeight: 700, color: TEXT, marginTop: 6, lineHeight: 1 }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: MUTED, marginTop: 6 }}>{sub}</div>}
  </Card>
);

interface Costs {
  ok: boolean;
  llm: { byFeature: { feature: string; costUsd: number }[]; totalUsd: number };
  compute: { totalUsd: number; activeSeconds: number; running: boolean };
  totalUsd: number;
}

const COST_LABEL: Record<string, string> = {
  planner: 'Planning conversations', prd: 'PRD drafting', designer: 'Design & mockups',
  builder: 'Build execution', direct: 'Engineer sessions',
};

const usd = (n: number) => n >= 100 ? `$${Math.round(n)}` : n >= 1 ? `$${n.toFixed(2)}` : `$${n.toFixed(3)}`;

const BuildStatsDashboard: React.FC<{ projectId?: string; clientId?: string; embedded?: boolean }> = ({ projectId, clientId, embedded }) => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [costs, setCosts] = useState<Costs | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(null);
    // Fetch by explicit project (workspace tab) or let the backend resolve the
    // client's primary project (/dashboard, no projects-list dependency).
    const req = projectId
      ? agentsApi.getBuildStats(projectId)
      : agentsApi.getClientBuildStats(clientId);
    req
      .then((r) => { if (alive) { if (r.data?.ok) setStats(r.data); else setError('no_data'); } })
      .catch(() => { if (alive) setError('failed'); })
      .finally(() => { if (alive) setLoading(false); });
    // Cost roll-up is best-effort and renders its own section only when present.
    const costReq = projectId
      ? agentsApi.getBuildCosts(projectId)
      : agentsApi.getClientBuildCosts(clientId);
    costReq
      .then((r) => { if (alive && r.data?.ok) setCosts(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [projectId, clientId]);

  const autonomyPct = useMemo(() => stats?.autonomy.rate != null ? Math.round(stats.autonomy.rate * 100) : null, [stats]);

  if (loading) return <div style={{ padding: 40, color: MUTED }}>Loading your agent's work…</div>;
  if (error || !stats || stats.ticketCount === 0) {
    return (
      <div style={{ padding: 48, textAlign: 'center', color: MUTED }}>
        <div style={{ fontFamily: "'IBM Plex Serif', serif", fontStyle: 'italic', fontSize: 18, color: TEXT }}>
          Your first build will appear here.
        </div>
        <div style={{ marginTop: 8, fontSize: 13 }}>Once your agent starts shipping work, this is where you'll see exactly what it did.</div>
      </div>
    );
  }

  const s = stats;
  const grid = (cols: string): React.CSSProperties => ({ display: 'grid', gridTemplateColumns: cols, gap: 16, marginBottom: 28 });

  return (
    <div style={{ color: TEXT, fontFamily: "'IBM Plex Sans', sans-serif" }}>
      {/* Hero — full page only; on /dashboard the section heading leads. */}
      {!embedded && (
        <div style={{ marginBottom: 28 }}>
          <Eyebrow>Layer 01 · your build</Eyebrow>
          <div style={{ fontSize: 26, fontWeight: 700 }}>
            Your agent shipped{' '}
            <span style={{ fontFamily: "'IBM Plex Serif', serif", fontStyle: 'italic', borderBottom: `3px solid ${YELLOW}`, paddingBottom: 1 }}>real, verified work</span>.
          </div>
        </div>
      )}
      <div style={grid('repeat(3, 1fr)')}>
        <Counter label="Files changed" value={s.hero.filesChanged} sub={`${s.depth.commandsRun} commands run`} />
        <Counter label="Verification gates passed" value={s.verification.acPassed || '—'} sub={s.verification.acPassed ? 'checks the agent ran before shipping' : 'recorded on newer builds'} />
        <Counter label="Completed autonomously" value={s.hero.completedAutonomously} sub={`of ${s.autonomy.terminal} tickets`} />
      </div>

      {/* Autonomy ring + escalation reasons */}
      <div style={grid('5fr 7fr')}>
        <Card>
          <Eyebrow>Autonomy</Eyebrow>
          {autonomyPct != null && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
              <div style={{
                width: 156, height: 156, borderRadius: '50%',
                background: `conic-gradient(${BLUE} ${autonomyPct * 3.6}deg, ${TRACK} 0deg)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <div style={{ width: 116, height: 116, borderRadius: '50%', background: CARD, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ fontSize: 38, fontWeight: 700, lineHeight: 1 }}>{autonomyPct}%</div>
                  <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 9, letterSpacing: '0.12em', color: MUTED, marginTop: 4 }}>AUTONOMOUS</div>
                </div>
              </div>
            </div>
          )}
          <div style={{ fontFamily: "'IBM Plex Serif', serif", fontStyle: 'italic', fontSize: 13, color: MUTED, marginTop: 16, textAlign: 'center' }}>
            We finished most of this without you. We flagged the rest on purpose.
          </div>
        </Card>
        <Card>
          <Eyebrow>When a human was needed</Eyebrow>
          {s.escalationReasons.length === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: GREEN, marginTop: 24, fontSize: 14 }}>
              <span style={{ fontSize: 18 }}>●</span> No human help needed yet.
            </div>
          ) : (
            <div style={{ marginTop: 10 }}>
              {s.escalationReasons.map((e, i) => {
                const max = Math.max(...s.escalationReasons.map((x) => x.count));
                return (
                  <div key={e.label} style={{ marginBottom: 14 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 5 }}>
                      <span>{e.label}</span><span style={{ fontFamily: "'IBM Plex Sans', sans-serif", color: MUTED }}>{e.count}</span>
                    </div>
                    <div style={{ height: 8, background: TRACK, borderRadius: 99 }}>
                      <div style={{ width: `${(e.count / max) * 100}%`, height: '100%', background: SERIES[i % SERIES.length], borderRadius: 99 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* What we built — flat segmented proportion bar + two-column legend */}
      <Card style={{ marginBottom: 28 }}>
        <Eyebrow>What we built · {s.ticketCount} tickets</Eyebrow>
        <div style={{ display: 'flex', gap: 3, height: 30, borderRadius: 8, overflow: 'hidden', marginTop: 6, marginBottom: 20 }}>
          {s.workMix.map((w, i) => (
            <div key={w.type} title={`${TYPE_LABEL[w.type] || w.type}: ${w.count}`}
              style={{ flexGrow: w.count, flexBasis: 0, minWidth: 8, background: SERIES[i % SERIES.length] }} />
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 40, rowGap: 0 }}>
          {s.workMix.map((w, i) => {
            const pctv = s.ticketCount ? Math.round((w.count / s.ticketCount) * 100) : 0;
            return (
              <div key={w.type} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 0', borderBottom: `1px solid ${TRACK}` }}>
                <span style={{ width: 11, height: 11, borderRadius: 3, background: SERIES[i % SERIES.length], flexShrink: 0 }} />
                <span style={{ flex: 1, fontSize: 14 }}>{TYPE_LABEL[w.type] || w.type}</span>
                <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontWeight: 700, fontSize: 14 }}>{pctv}%</span>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Momentum — clean column bars, no axes */}
      {s.momentum.length > 0 && (
        <Card style={{ marginBottom: 28 }}>
          <Eyebrow>Momentum · tickets per week</Eyebrow>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, height: 200, marginTop: 14 }}>
            {(() => {
              const maxV = Math.max(...s.momentum.map((m) => m.planned), 1);
              return s.momentum.map((m) => {
                const planRemain = Math.max(m.planned - m.delivered, 0);
                return (
                  <div key={m.week} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end' }}>
                    <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12, fontWeight: 700, marginBottom: 6 }}>{m.delivered}</div>
                    <div style={{ width: '100%', maxWidth: 56, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: 150 }}>
                      {planRemain > 0 && <div style={{ height: `${(planRemain / maxV) * 100}%`, background: YELLOW, borderRadius: '7px 7px 0 0' }} />}
                      <div style={{ height: `${(m.delivered / maxV) * 100}%`, background: BLUE, borderRadius: planRemain > 0 ? 0 : '7px 7px 0 0' }} />
                    </div>
                    <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 10, color: MUTED, marginTop: 10 }}>{m.week.replace(/^\d+-/, '')}</div>
                  </div>
                );
              });
            })()}
          </div>
          <div style={{ display: 'flex', gap: 18, marginTop: 14, fontSize: 12, color: MUTED }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: BLUE }} /> Delivered</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: YELLOW }} /> Still planned</span>
          </div>
        </Card>
      )}

      {/* Depth + planner rigor */}
      <div style={grid('repeat(4, 1fr)')}>
        <Counter label="Files touched" value={s.depth.filesChanged} />
        <Counter label="Commands run" value={s.depth.commandsRun} />
        <Counter label="Pull requests" value={s.depth.prsTotal} sub={s.depth.prsMerged ? `${s.depth.prsMerged} merged` : undefined} />
        <Counter label="Subtasks done" value={s.depth.subtasksDone ? `${s.depth.subtasksDone}/${s.depth.subtasksTotal}` : '—'} sub={s.depth.subtasksTotal ? undefined : 'tracked on newer builds'} />
      </div>

      <div style={grid('6fr 6fr')}>
        <Card>
          <Eyebrow>Planner rigor</Eyebrow>
          <div style={{ fontSize: 14, color: TEXT }}>Before writing each plan, the agent digested <b>{s.plannerRigor.avgFilesDigested}</b> files of your code on average.</div>
          {s.plannerRigor.coverage && (
            <div style={{ marginTop: 18 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                <span>Requirements scoped into tickets</span>
                <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", color: MUTED }}>{s.plannerRigor.coverage.covered} of {s.plannerRigor.coverage.total}</span>
              </div>
              <div style={{ height: 10, background: TRACK, borderRadius: 5 }}>
                <div style={{ width: `${(s.plannerRigor.coverage.covered / s.plannerRigor.coverage.total) * 100}%`, height: '100%', background: BLUE, borderRadius: 5 }} />
              </div>
            </div>
          )}
        </Card>
        <Card>
          <Eyebrow>Recent activity</Eyebrow>
          <div style={{ maxHeight: 200, overflowY: 'auto' }}>
            {s.ledger.slice(0, 12).map((l, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, padding: '7px 0', borderBottom: `1px solid ${TRACK}`, fontSize: 13 }}>
                <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 10, color: MUTED, minWidth: 64 }}>
                  {l.createdAt ? new Date(l.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}
                </span>
                <span style={{ color: TEXT }}>{l.title}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* What it cost — per-feature LLM spend + sandbox compute. At-cost,
          billed to this account; only renders when the roll-up is available. */}
      {costs && costs.totalUsd > 0 && (
        <Card style={{ marginBottom: 28 }}>
          <Eyebrow>What it cost</Eyebrow>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
            <div style={{ fontSize: 34, fontWeight: 700, lineHeight: 1 }}>{usd(costs.totalUsd)}</div>
            <div style={{ fontSize: 12, color: MUTED }}>
              total to date · {usd(costs.llm.totalUsd)} agent work + {usd(costs.compute.totalUsd)} compute
              {costs.compute.running ? ' (workspace running now)' : ''}
            </div>
          </div>
          <div style={{ marginTop: 16 }}>
            {costs.llm.byFeature.filter((f) => f.costUsd > 0).map((f, i) => {
              const max = Math.max(...costs.llm.byFeature.map((x) => x.costUsd), 0.0001);
              return (
                <div key={f.feature} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 5 }}>
                    <span>{COST_LABEL[f.feature] || f.feature}</span>
                    <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", color: MUTED }}>{usd(f.costUsd)}</span>
                  </div>
                  <div style={{ height: 8, background: TRACK, borderRadius: 99 }}>
                    <div style={{ width: `${(f.costUsd / max) * 100}%`, height: '100%', background: SERIES[i % SERIES.length], borderRadius: 99 }} />
                  </div>
                </div>
              );
            })}
            <div style={{ marginBottom: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 5 }}>
                <span>Workspace compute ({Math.round(costs.compute.activeSeconds / 3600)}h active)</span>
                <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", color: MUTED }}>{usd(costs.compute.totalUsd)}</span>
              </div>
              <div style={{ height: 8, background: TRACK, borderRadius: 99 }}>
                <div style={{ width: `${Math.min((costs.compute.totalUsd / Math.max(costs.totalUsd, 0.0001)) * 100, 100)}%`, height: '100%', background: '#9b7bff', borderRadius: 99 }} />
              </div>
            </div>
          </div>
        </Card>
      )}

      <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 10, color: MUTED, textAlign: 'right', marginTop: 4 }}>
        BASED ON {s.ticketCount} TICKETS · LIVE FROM YOUR BUILD WORKSPACE
      </div>
    </div>
  );
};

export default BuildStatsDashboard;
